import type { Project } from '@finsim/model';
import { createEffect, createEvent, createStore, sample } from 'effector';
import { debounce } from 'patronum';
import { createMcWorker, proxyCallback, type McWorker, type MonteCarloResult } from '../../worker';
import { $effectiveProject } from '../item-visibility';
import { projectClosed } from '../project';
import { $simMode } from '../view-settings';

/*
 * Monte Carlo runs (docs/PLAN.md §3.5): in Monte Carlo mode the effective project is simulated
 * again shortly after edits settle. A run lives in its own worker; starting a new run or
 * cancelling terminates it (the only way to stop a busy worker). Results and progress carry a
 * runId, and only the latest run's reach the stores. The previous result stays on screen (dimmed)
 * while a new one is computed.
 */

/** Quiet time after an edit before a new run starts. */
export const MC_DEBOUNCE_MS = 400;

export class McCancelledError extends Error {
  constructor() {
    super('Monte Carlo run cancelled');
    this.name = 'McCancelledError';
  }
}

let worker: McWorker | null = null;
let rejectRun: ((error: Error) => void) | null = null;

/** Stops the run in flight, if any (its promise rejects with McCancelledError). */
function stopRun(): void {
  if (!rejectRun) return;
  rejectRun(new McCancelledError());
  rejectRun = null;
  worker?.terminate();
  worker = null;
}

export const mcRequested = createEvent();
export const mcCancelled = createEvent();
export const mcProgressed = createEvent<{ runId: number; fraction: number }>();

export const runMcFx = createEffect(
  ({ project, runId }: { project: Project; runId: number }): Promise<MonteCarloResult> =>
    new Promise<MonteCarloResult>((resolve, reject) => {
      stopRun();
      const current = (worker ??= createMcWorker());
      rejectRun = reject;
      const onProgress = proxyCallback((fraction: number) => {
        mcProgressed({ runId, fraction });
      });
      current.api.simulate(project, { runId }, onProgress).then(
        (result) => {
          rejectRun = null;
          resolve(result);
        },
        (error: unknown) => {
          // A crashed worker is replaced on the next run.
          rejectRun = null;
          current.terminate();
          if (worker === current) worker = null;
          reject(error instanceof Error ? error : new Error(String(error)));
        },
      );
    }),
);

export const cancelMcFx = createEffect(stopRun);

const $mcRunId = createStore(0).on(mcRequested, (n) => n + 1);

export const $mcResult = createStore<MonteCarloResult | null>(null).reset(projectClosed);
/** Fraction done of the latest run, or null when none is running. */
export const $mcProgress = createStore<number | null>(null);
export const $mcError = createStore<string | null>(null);
/** A run is in progress (changes only when one starts or ends, unlike $mcProgress). */
export const $mcRunning = $mcProgress.map((p) => p !== null);

// ── When to run ───────────────────────────────────────────────────────────────
sample({
  clock: $simMode.updates,
  filter: (mode) => mode === 'monteCarlo',
  target: mcRequested,
});
const editedInMcMode = sample({
  clock: $effectiveProject.updates,
  source: $simMode,
  filter: (mode, project) => mode === 'monteCarlo' && project !== null,
});
sample({ clock: debounce(editedInMcMode, MC_DEBOUNCE_MS), target: mcRequested });

sample({
  clock: mcRequested,
  source: { project: $effectiveProject, runId: $mcRunId },
  filter: ({ project }) => project !== null,
  fn: ({ project, runId }) => ({ project: project as Project, runId }),
  target: runMcFx,
});

sample({
  clock: [mcCancelled, projectClosed, $simMode.updates.filter({ fn: (m) => m !== 'monteCarlo' })],
  target: cancelMcFx,
});

// ── Results and status ─────────────────────────────────────────────────────────
sample({
  clock: runMcFx.doneData,
  source: $mcRunId,
  filter: (latest, result) => result.runId === latest,
  fn: (_, result) => result,
  target: $mcResult,
});

$mcProgress.on(runMcFx, () => 0).reset(projectClosed);
// A run replaced by a newer one also settles (cancelled): only the latest run ends progress.
const latestSettled = sample({
  clock: runMcFx.finally,
  source: $mcRunId,
  filter: (latest, { params }) => params.runId === latest,
  fn: (_, settled) => settled,
});
sample({ clock: latestSettled, fn: () => null, target: $mcProgress });
sample({
  clock: mcProgressed,
  source: { latest: $mcRunId, progress: $mcProgress },
  filter: ({ latest, progress }, p) => p.runId === latest && progress !== null,
  fn: (_, p) => p.fraction,
  target: $mcProgress,
});

$mcError
  .on(latestSettled, (current, settled) =>
    settled.status === 'done'
      ? null
      : settled.error instanceof McCancelledError
        ? current
        : settled.error.message,
  )
  .reset(projectClosed);
